import * as React from "react";
import _ from 'underscore';

type MessageContextType = {
  messageOpen: boolean;
  messageContent: string | null;
  successOpen: boolean;
  successContent: string | null;
  show: (message: any, timeout?: number) => void;
  showSuccess: (message?: string, timeout?: number) => void;
  clear: () => void;
};

const MessageContext = React.createContext<MessageContextType | null>(null);

interface ProviderProps {
  children: React.ReactNode;
}

function Provider(props: ProviderProps) {
  const [messageOpen, setMessageOpen] = React.useState(false);
  const [messageContent, setMessageContent] = React.useState<string | null>(null);
  const [successOpen, setSuccessOpen] = React.useState(false);
  const [successContent, setSuccessContent] = React.useState<string | null>(null);

  // Without these, a second toast inherits the first one's countdown and can be
  // dismissed early — so a message is not guaranteed to stay for its timeout.
  const messageTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const successTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  React.useEffect(() => () => {
    if (messageTimer.current) clearTimeout(messageTimer.current);
    if (successTimer.current) clearTimeout(successTimer.current);
  }, []);

  /**
   * @param message Message to display
   * @param timeout Time to persist message (in seconds)
   */
  const show = (message: any, timeout: number = 5) => {
    timeout = timeout * 1000;
    if (_.isObject(message)) {
      message = "Something unexpected happened. Please try again later.";
    }
    setMessageContent(message);
    setMessageOpen(true);
    if (messageTimer.current) clearTimeout(messageTimer.current);
    messageTimer.current = setTimeout(() => {
      setMessageContent(null);
      setMessageOpen(false);
    }, timeout);
  };

  const showSuccess = (message: string = 'Successful operation', timeout: number = 5) => {
    timeout = timeout * 1000;
    setSuccessContent(message);
    setSuccessOpen(true);
    if (successTimer.current) clearTimeout(successTimer.current);
    successTimer.current = setTimeout(() => {
      setSuccessContent(null);
      setSuccessOpen(false);
    }, timeout);
  };

  /**
   * Clear any current messages
   */
  const clear = () => {
    if (messageTimer.current) clearTimeout(messageTimer.current);
    if (successTimer.current) clearTimeout(successTimer.current);
    setSuccessContent(null);
    setSuccessOpen(false);
    setMessageOpen(false);
    setMessageContent(null);
  };

  const value = {
    messageOpen,
    messageContent,
    successOpen,
    successContent,
    show,
    showSuccess,
    clear,
  };

  return (
    <MessageContext.Provider value={value}>
      {props.children}
      {messageOpen && (
        <div className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 max-w-md mx-auto z-50">
          <div className="flex items-center justify-between gap-3 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded shadow-sm">
            <span className="block text-sm leading-normal">{messageContent}</span>
            <button
              onClick={clear}
              className="flex-shrink-0 text-red-500 hover:text-red-700 transition-colors p-0.5 rounded focus:outline-none"
              aria-label="Close"
            >
              <svg className="fill-current h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                <title>Close</title>
                <path d="M14.348 14.849a1.2 1.2 0 0 1-1.697 0L10 11.819l-2.651 3.029a1.2 1.2 0 1 1-1.697-1.697l2.758-3.15-2.759-3.152a1.2 1.2 0 1 1 1.697-1.697L10 8.183l2.651-3.031a1.2 1.2 0 1 1 1.697 1.697l-2.758 3.152 2.758 3.15a1.2 1.2 0 0 1 0 1.698z" />
              </svg>
            </button>
          </div>
        </div>
      )}
      {successOpen && (
        <div className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 max-w-md mx-auto z-50">
          <div className="flex items-center justify-between gap-3 bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded shadow-sm">
            <span className="block text-sm leading-normal">{successContent}</span>
            <button
              onClick={clear}
              className="flex-shrink-0 text-green-600 hover:text-green-800 transition-colors p-0.5 rounded focus:outline-none"
              aria-label="Close"
            >
              <svg className="fill-current h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                <title>Close</title>
                <path d="M14.348 14.849a1.2 1.2 0 0 1-1.697 0L10 11.819l-2.651 3.029a1.2 1.2 0 1 1-1.697-1.697l2.758-3.15-2.759-3.152a1.2 1.2 0 1 1 1.697-1.697L10 8.183l2.651-3.031a1.2 1.2 0 1 1 1.697 1.697l-2.758 3.152 2.758 3.15a1.2 1.2 0 0 1 0 1.698z" />
              </svg>
            </button>
          </div>
        </div>
      )}
    </MessageContext.Provider>
  );
}

function useMessage() {
  const context = React.useContext(MessageContext);
  if (context === null) {
    throw new Error('useMessage must be used within a MessageProvider');
  }
  return context;
}

export default {
  Provider,
  useMessage,
};